import { env } from '@/lib/env';
import type { PreviewFeature } from '@/types';

/**
 * The release-scope gate (ADR-0034). A capability outside the release is shown only when
 * `VITE_PREVIEW_FEATURES` names it, and every deployed environment leaves that unset, so the
 * default is the shipping scope.
 *
 * The set is read once from env and held here, the shape `tokenStore` uses, so that every gate —
 * a route, a nav entry, a tab, a tile, a menu item — asks one synchronous question. It lives in
 * `config` because routes, nav, pages and features all need it and all may import `config`.
 */
let enabled: ReadonlySet<PreviewFeature> = new Set(env.VITE_PREVIEW_FEATURES);

export function isFeatureEnabled(feature: PreviewFeature): boolean {
  return enabled.has(feature);
}

/** Refuses outside Vitest, so no production caller can widen the release scope at runtime. */
function assertTestMode(): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error('previewFeatureStore is test-only; set VITE_PREVIEW_FEATURES instead.');
  }
}

/** Test-only — reach it through `setPreviewFeatures` (`src/test/utils/preview-features.ts`), which
 *  also restores the env value when the test finishes. */
export const previewFeatureStore = {
  set(features: readonly PreviewFeature[]): void {
    assertTestMode();
    enabled = new Set(features);
  },
  reset(): void {
    assertTestMode();
    enabled = new Set(env.VITE_PREVIEW_FEATURES);
  },
};

/** `items` when `feature` is enabled, otherwise nothing — for a run of route objects or list
 *  entries that belong to one capability, spread into the list that holds them. */
export function whenEnabled<T>(feature: PreviewFeature, items: readonly T[]): T[] {
  return isFeatureEnabled(feature) ? [...items] : [];
}

/** Drops each entry tagged with a disabled capability. An untagged entry is always kept. */
export function onlyEnabled<T extends { feature?: PreviewFeature }>(items: readonly T[]): T[] {
  return items.filter((item) => !item.feature || isFeatureEnabled(item.feature));
}
